import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./App.css";

function App() {
  // Register ScrollTrigger plugin
  gsap.registerPlugin(ScrollTrigger);
  
  // Refs for the elements we want to animate
  const appImageRef = useRef(null);
  const appContentRef = useRef(null);
  const aboutContentRef = useRef(null);
  const emailButtonRef = useRef(null);
  
  useEffect(() => {
    // Animation logic for hero section fade-in effects
    const tagline = document.querySelector(".hero-tagline");
    const headshot = document.querySelector(".hero-image");
    
    // Apply animations with setTimeout to ensure DOM is ready
    setTimeout(() => {
      tagline.style.opacity = "1";
      
      setTimeout(() => {
        headshot.style.opacity = "1";
        headshot.style.transform = "translateY(0)";
      }, 200); // Slight delay for image animation
    }, 100);

    // GSAP ScrollTrigger animations for app section
    // Reset any existing ScrollTriggers to prevent duplicates
    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    
    // Check if we're on mobile
    const isMobile = window.innerWidth <= 768;
    
    if (isMobile) {
      // Mobile animations (vertical)
      gsap.fromTo(
        appImageRef.current,
        {
          y: -50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: ".app-section",
            start: "top 75%",
            end: "top 25%",
            scrub: 1,
          }
        }
      );
      
      gsap.fromTo(
        appContentRef.current,
        {
          y: 50,
          opacity: 0
        },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: ".app-section",
            start: "top 60%",
            end: "top 10%",
            scrub: 1,
          }
        }
      );
    } else {
      // Desktop animations (horizontal)
      gsap.fromTo(
        appImageRef.current,
        {
          x: -100,
          opacity: 0
        },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: ".app-section",
            start: "top 75%", // Start when the top of app-section reaches 75% from the top of viewport
            end: "top 25%", // End when the top of app-section reaches 25% from the top of viewport
            scrub: 1, // Smooth scrubbing, takes 1 second to catch up to the scrollbar
            // markers: true, // For debugging
          }
        }
      );
      
      gsap.fromTo(
        appContentRef.current,
        {
          x: 100,
          opacity: 0
        },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: ".app-section",
            start: "top 75%",
            end: "top 25%",
            scrub: 1,
            // markers: true, // For debugging
          }
        }
      );
    }
    
    // Animation for about section
    gsap.fromTo(
      aboutContentRef.current,
      {
        y: 50,
        opacity: 0
      },
      {
        y: 0,
        opacity: 1,
        duration: 1,
        scrollTrigger: {
          trigger: ".about-section",
          start: "top 80%",
          end: "top 30%",
          scrub: 1,
        }
      }
    );
    
    // Animation for email button
    gsap.fromTo(
      emailButtonRef.current,
      {
        y: 20,
        opacity: 0
      },
      {
        y: 0,
        opacity: 1,
        duration: 0.8,
        scrollTrigger: {
          trigger: ".footer",
          start: "top 90%",
          end: "top 70%",
          scrub: 1,
        }
      }
    );
    
    // Handle window resize to refresh animations
    const handleResize = () => {
      // Refresh all ScrollTriggers
      ScrollTrigger.refresh();
    };
    
    window.addEventListener('resize', handleResize);
    
    // Clean up ScrollTrigger instances when component unmounts
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div className="App">
      <header className="site-header">
        <div className="header-container">
          <h2 className="company-name">Dancing Bear Software</h2>
          <div className="header-border"></div>
        </div>
      </header>
      
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-tagline">
            <h1>
              Crafting AI that works for humans, not the other way around.
            </h1>
          </div>
          <div className="hero-image-container">
            <img
              src="/images/headshot.jpg"
              alt="Profile headshot"
              className="hero-image"
            />
          </div>
        </div>
      </div>

      <div className="app-section">
        <div className="app-container">
          <div className="app-image-container" ref={appImageRef}>
            <div className="iphone-frame">
              <img
                src="/images/medmetaSquare.png"
                alt="Medmeta"
                className="app-image"
              />
            </div>
          </div>
          <div className="app-content" ref={appContentRef}>
            <h2>Check out our flagship app Medmeta</h2>
            <p>
              MedMeta transforms complex medical research into accessible,
              actionable insights for healthcare professionals through
              AI-powered synthesis of recent journal articles. By distilling the
              latest medical literature into various educational formats, it
              helps doctors and medical students develop a broader understanding
              of industry consensus across multiple topics, preparing them for
              effective collaboration with AI systems while maintaining crucial
              human oversight.
            </p>
            <a
              href="https://medmeta.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="cta-button"
            >
              Learn More
            </a>
          </div>
        </div>
      </div>
      
      <div className="about-section">
        <div className="about-container">
          <div className="about-content" ref={aboutContentRef}>
            <h2>About Dancing Bear Software</h2>
            <p>
              Dancing Bear Software is the creation of Eli, a solo developer with 5 years of software engineering and acceleration experience at a major organization, including 2 years specializing in generative AI technologies. As an independent developer, Eli brings enterprise-level engineering practices to innovative app development with a laser focus on solving genuine problems for real people.
            </p>
            <p>
              Founded on the principle that sophisticated technical solutions don't require large teams, Dancing Bear Software emphasizes quality craftsmanship, robust architecture, and thoughtful design in creating products that address authentic human needs. Our flagship application, MedMeta, demonstrates our commitment to building impactful tools that tackle meaningful challenges faced by healthcare professionals in their daily work.
            </p>
            <p>
              Dancing Bear Software represents Eli's vision of combining deep technical expertise with creative problem-solving that makes a tangible difference in users' lives. Each project begins by understanding real-world pain points and ends with meticulous implementation, from clean code architecture to intuitive user experiences. While operating as a solo developer, Eli approaches each challenge with the discipline and rigor of large-scale engineering teams, but with the agility and user empathy often missing in bigger organizations.
            </p>
            <p>
              Our foundation in acceleration work and generative AI powers our ability to build sophisticated applications that punch above their weight while remaining grounded in practical utility. Dancing Bear Software is dedicated to proving that a single passionate developer with the right expertise can create products that not only rival those of much larger companies but also connect more deeply with the people who use them—bringing solutions to life that matter in the real world.
            </p>
          </div>
        </div>
      </div>
      
      <footer className="footer">
        <a 
          href="mailto:eli@dancingbearsoftware.com" 
          className="email-button"
          ref={emailButtonRef}
        >
          <span className="email-icon">✉</span>
          Contact Me
        </a>
      </footer>
    </div>
  );
}

export default App;
